import React, { useState, useEffect } from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { FileText, Download, Image, FileDown } from 'lucide-react';

// Import media kit assets
import arcaLogo from '../../assets/media-kit/arca_logo.png';
import arcaFactSheet from '../../assets/media-kit/ARCA FACT SHEET.pdf';
import arcaFrench from '../../assets/media-kit/ARCA(French).pdf';
import arcaPortuguese from '../../assets/media-kit/ARCA(Portuguese).pdf';
import arcaArabic from '../../assets/media-kit/ARCA(Arabic).pdf';

const MediaKitPage = () => {
  const [mediaResources, setMediaResources] = useState([]);
  const [quickFacts, setQuickFacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Icon mapping
  const iconMap = {
    Image: Image,
    FileText: FileText,
    FileDown: FileDown,
    Download: Download
  };

  useEffect(() => {
    fetchMediaKitData();
  }, []);

  const fetchMediaKitData = async () => {
    try {
      setLoading(true);
      const [resourcesRes, factsRes] = await Promise.all([
        fetch(`${API_BASE}/media-kit/resources`),
        fetch(`${API_BASE}/media-kit/quick-facts`)
      ]);

      const [resourcesData, factsData] = await Promise.all([
        resourcesRes.json(),
        factsRes.json()
      ]);

      if (resourcesData.success) {
        setMediaResources(resourcesData.data || []);
      }

      if (factsData.success) {
        setQuickFacts(factsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching media kit data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Merge API resources with static downloadable files
  const mergedResources = [...mediaResources];

  // Add logo to ARCA Logos and ARCA Logo Package resources if they exist
  const arcaLogosResource = mergedResources.find(r => r.title === 'ARCA Logos');
  if (arcaLogosResource) {
    arcaLogosResource.staticFiles = arcaLogosResource.staticFiles || [];
    arcaLogosResource.staticFiles.push({
      id: 'logo',
      title: 'ARCA Logo',
      file: arcaLogo,
      fileName: 'ARCA_Logo.png',
      icon: 'Image'
    });
  }

  const arcaLogoPackageResource = mergedResources.find(r => r.title === 'ARCA Logo Package');
  if (arcaLogoPackageResource) {
    arcaLogoPackageResource.staticFiles = arcaLogoPackageResource.staticFiles || [];
    arcaLogoPackageResource.staticFiles.push({
      id: 'logo-package',
      title: 'ARCA Logo',
      file: arcaLogo,
      fileName: 'ARCA_Logo.png',
      icon: 'Image'
    });
  }

  // Add fact sheets to Fact Sheets resource if it exists
  const factSheetsResource = mergedResources.find(r => r.title === 'Fact Sheets');
  if (factSheetsResource) {
    factSheetsResource.staticFiles = factSheetsResource.staticFiles || [];
    factSheetsResource.staticFiles.push(
      {
        id: 'factsheet-en',
        title: 'ARCA FACT SHEET',
        file: arcaFactSheet,
        fileName: 'ARCA_Fact_Sheet.pdf',
        icon: 'FileText'
      }
    );
  }

  // Update ARCA in Other Languages resource if it exists
  const otherLanguagesResource = mergedResources.find(r => r.title === 'ARCA in French' || r.title === 'ARCA in Other Languages');
  if (otherLanguagesResource) {
    otherLanguagesResource.title = 'ARCA in Other Languages'; // Update title
    otherLanguagesResource.staticFiles = otherLanguagesResource.staticFiles || [];
    otherLanguagesResource.staticFiles.push(
      {
        id: 'french',
        title: 'Français',
        file: arcaFrench,
        fileName: 'ARCA_French.pdf',
        icon: 'FileText'
      },
      {
        id: 'portuguese',
        title: 'Português',
        file: arcaPortuguese,
        fileName: 'ARCA_Portuguese.pdf',
        icon: 'FileText'
      },
      {
        id: 'arabic',
        title: 'العربية',
        file: arcaArabic,
        fileName: 'ARCA_Arabic.pdf',
        icon: 'FileText'
      }
    );
  }

  const handleDownload = (file, fileName) => {
    const link = document.createElement('a');
    link.href = file;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <PublicLayout>
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <FileText className="text-white" size={40} />
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">Media Kit</h1>
              <p className="lead text-muted">
                Resources for journalists, partners, and media professionals
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Available Resources */}
              {mergedResources.length > 0 && (
                <div className="row justify-content-center mb-5">
                  <div className="col-lg-10">
                    <h3 className="h4 fw-bold text-dark mb-4">Available Resources</h3>
                    <div className="row g-4">
                      {mergedResources.map((resource, index) => {
                        const Icon = iconMap[resource.icon] || Image;
                        return (
                          <div key={resource.id || index} className="col-md-6">
                            <div className="card h-100 shadow-sm border-0">
                              <div className="card-body p-4">
                                <div className="d-flex align-items-start mb-3">
                                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                                    style={{ width: '56px', height: '56px', backgroundColor: '#e8f5e9' }}>
                                    <Icon style={{ color: '#6B8E23' }} size={28} />
                                  </div>
                                  <div className="flex-grow-1">
                                    <h5 className="fw-bold text-dark mb-2">{resource.title}</h5>
                                    <p className="text-muted mb-3">{resource.description}</p>

                                    {/* Resource items */}
                                    {resource.items && resource.items.length > 0 && (
                                      <ul className="list-unstyled mb-3">
                                        {resource.items.map((item, idx) => (
                                          <li key={idx} className="small text-muted mb-1">
                                            <i className="fas fa-check-circle me-2" style={{ color: '#6B8E23' }}></i>
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    )}

                                    {/* Static downloadable files */}
                                    {resource.staticFiles && resource.staticFiles.length > 0 && (
                                      <div className="mb-3">
                                        <div className="d-flex flex-wrap gap-2">
                                          {resource.staticFiles.map((file) => (
                                            <button
                                              key={file.id}
                                              onClick={() => handleDownload(file.file, file.fileName)}
                                              className="btn btn-sm text-white"
                                              style={{ backgroundColor: '#6B8E23' }}
                                            >
                                              <Download size={14} className="me-1" />
                                              {file.title}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Status badge only */}
                                    <div className="d-flex align-items-center gap-2">
                                      <span className={`badge ${resource.status === 'Available' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                        {resource.status}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default MediaKitPage;
